// src/app/services/websocket/websocket.service.ts
import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import { StorageService } from '../storage-service/storage.service';

declare var SockJS: any;
declare var Stomp: any;

export interface SolicitacaoExclusao {
    id?: number;
    numeroPapeleta: string;
    nomeMotorista: string;
    matricula: string;
    dataHoraSolicitacao?: string;
    status?: string;
    tipoMarcacao?: string;
    descricaoAlteracao?: string;
    folhaServicoId?: number;
}

export interface SolicitacaoExclusaoDTO {
    id?: number;
    numeroPapeleta: string;
    nomeMotorista: string;
    matricula: string;
    dataHoraSolicitacao?: string;
    status?: string; // PENDENTE, ACEITA, NEGADA
    tipoMarcacao?: string;
    descricaoAlteracao?: string;
    dataHoraAtendimento?: string;
    atendidoPor?: string;
}

export interface NotificacaoStatus {
    id?: number;
    numeroPapeleta: string;
    nomeMotorista: string;
    matricula: string;
    status: string;
    dataHoraAtendimento?: string;
    atendidoPor?: string;
}

@Injectable({
    providedIn: 'root'
})
export class WebSocketService {
    private stompClient: any;
    private solicitacoesSubject = new Subject<SolicitacaoExclusao>();
    private notificacoesStatusSubject = new Subject<NotificacaoStatus>();
    private connectionStatus = new Subject<boolean>();
    private carregandoBibliotecas: boolean = false;
    private apiBaseUrl: string = '';
    private wsBaseUrl: string = ''; // Será http:// ou https:// (SockJS usa isso)

    constructor(
        private http: HttpClient,
        private storageService: StorageService
    ) {
        this.inicializarUrls();
    }

    private async inicializarUrls() {
        try {
            const servidor = await this.storageService.getServidor();
            if (servidor && servidor.url_servidor) {
                // Remove a barra final se existir
                let baseUrl = servidor.url_servidor.replace(/\/$/, '');
                this.apiBaseUrl = `${baseUrl}/solicitacoes-exclusao`;

                // IMPORTANTE: SockJS usa URL HTTP/HTTPS, não WS/WSS
                // O SockJS internamente gerencia a conversão para WebSocket
                this.wsBaseUrl = `${baseUrl}/ws`;

                console.log('✅ URLs configuradas:', { api: this.apiBaseUrl, ws: this.wsBaseUrl });
                this.carregarBibliotecas();
            } else {
                console.error('❌ Servidor não configurado');
                setTimeout(() => this.inicializarUrls(), 1000);
            }
        } catch (error) {
            console.error('❌ Erro ao carregar configuração do servidor:', error);
            setTimeout(() => this.inicializarUrls(), 1000);
        }
    }

    private carregarBibliotecas() {
        if (this.carregandoBibliotecas) return;
        this.carregandoBibliotecas = true;

        if (typeof SockJS !== 'undefined' && typeof Stomp !== 'undefined') {
            this.conectar();
            return;
        }

        const scriptSockJS = document.createElement('script');
        scriptSockJS.src = 'https://cdn.jsdelivr.net/npm/sockjs-client@1/dist/sockjs.min.js';
        scriptSockJS.onload = () => {
            const scriptStomp = document.createElement('script');
            scriptStomp.src = 'https://cdnjs.cloudflare.com/ajax/libs/stomp.js/2.3.3/stomp.min.js';
            scriptStomp.onload = () => {
                console.log('✅ Bibliotecas WebSocket carregadas');
                this.conectar();
            };
            document.head.appendChild(scriptStomp);
        };
        document.head.appendChild(scriptSockJS);
    }

    conectar() {
        if (!this.wsBaseUrl) {
            console.log('⏳ Aguardando configuração da URL...');
            setTimeout(() => this.conectar(), 500);
            return;
        }

        if (typeof SockJS === 'undefined' || typeof Stomp === 'undefined') {
            setTimeout(() => this.conectar(), 500);
            return;
        }

        try {
            console.log('🔄 Conectando ao WebSocket em:', this.wsBaseUrl);
            // SockJS espera uma URL HTTP/HTTPS, não WS/WSS
            const socket = new SockJS(this.wsBaseUrl);
            this.stompClient = Stomp.over(socket);
            this.stompClient.debug = null;

            this.stompClient.connect({}, () => {
                console.log('✅ WebSocket conectado');
                this.connectionStatus.next(true);

                // Inscreve para solicitações de exclusão
                this.stompClient.subscribe('/topic/solicitacoes-exclusao', (message: any) => {
                    try {
                        const solicitacao: SolicitacaoExclusao = JSON.parse(message.body);
                        console.log('📨 Nova solicitação recebida:', solicitacao);
                        this.solicitacoesSubject.next(solicitacao);
                    } catch (e) {
                        console.error('Erro ao processar mensagem:', e);
                    }
                });

                // Inscreve para notificações de status (apenas se tiver matrícula)
                const matricula = this.getMatricula();
                if (matricula) {
                    this.stompClient.subscribe(`/topic/notificacoes/${matricula}`, (message: any) => {
                        try {
                            const notificacao: NotificacaoStatus = JSON.parse(message.body);
                            console.log('📨 Notificação de status recebida:', notificacao);
                            this.notificacoesStatusSubject.next(notificacao);
                        } catch (e) {
                            console.error('Erro ao processar notificação:', e);
                        }
                    });
                }
            }, (error: any) => {
                console.error('❌ Erro no WebSocket:', error);
                this.connectionStatus.next(false);
                setTimeout(() => this.conectar(), 5000);
            });
        } catch (error) {
            console.error('❌ Erro ao conectar WebSocket:', error);
            setTimeout(() => this.conectar(), 5000);
        }
    }

    private getMatricula(): string {
        try {
            const token = localStorage.getItem('token');
            if (token) {
                const decoded: any = jwtDecode(token);
                return decoded.sub || '';
            }
        } catch (error) {
            console.error('Erro ao decodificar token:', error);
        }
        return '';
    }

    /*async carregarSolicitacoesPendentes(): Promise<SolicitacaoExclusao[]> {
        if (!this.apiBaseUrl) {
            console.log('⏳ Aguardando configuração da API...');
            return [];
        }

        try {
            const url = `${this.apiBaseUrl}/pendentes`;
            console.log('📡 Chamando API:', url);

            const response = await firstValueFrom(this.http.get<SolicitacaoExclusao[]>(url));
            console.log('✅ Resposta da API:', response);
            return response;
        } catch (error) {
            console.error('❌ Erro ao carregar solicitações pendentes:', error);
            return [];
        }
    }*/

    async atualizarStatus(id: number, status: string, atendidoPor: string): Promise<void> {
        if (!this.apiBaseUrl) return;

        try {
            await firstValueFrom(
                this.http.put(`${this.apiBaseUrl}/${id}/status?status=${status}&atendidoPor=${atendidoPor}`, {})
            );
        } catch (error) {
            console.error('Erro ao atualizar status:', error);
        }
    }

    solicitarExclusaoAssinatura(solicitacao: {
        numeroPapeleta: string;
        nomeMotorista: string;
        matricula: string;
        usuario?: string;
        folhaServicoId?: number;
    }) {
        if (this.stompClient && this.stompClient.connected) {
            this.stompClient.send('/app/solicitar-exclusao-assinatura', {}, JSON.stringify(solicitacao));
            console.log('📤 Solicitação enviada:', solicitacao);
        } else {
            console.error('❌ WebSocket não conectado');
        }
    }

    enviarNotificacaoStatus(notificacao: NotificacaoStatus) {
        if (this.stompClient && this.stompClient.connected) {
            this.stompClient.send('/app/notificacao-status-assinatura', {}, JSON.stringify(notificacao));
            console.log('📤 Notificação de status enviada:', notificacao);
        } else {
            console.error('❌ WebSocket não conectado para enviar notificação');
        }
    }

    getSolicitacoes(): Observable<SolicitacaoExclusao> {
        return this.solicitacoesSubject.asObservable();
    }

    getNotificacoesStatus(): Observable<NotificacaoStatus> {
        return this.notificacoesStatusSubject.asObservable();
    }

    getConnectionStatus(): Observable<boolean> {
        return this.connectionStatus.asObservable();
    }

    isConnected(): boolean {
        return this.stompClient && this.stompClient.connected;
    }

    registrarAssinatura(dados: { numeroPapeleta: string; usuario: string }) {
        if (this.stompClient && this.stompClient.connected) {
            this.stompClient.send('/app/registrar-assinatura', {}, JSON.stringify(dados));
            console.log('📤 Assinatura registrada:', dados);
        } else {
            console.error('❌ WebSocket não conectado para registrar assinatura');
        }
    }

    async registrarAssinaturaHttp(numeroPapeleta: string, usuario: string): Promise<void> {
        if (!this.apiBaseUrl) return;

        try {
            await firstValueFrom(
                this.http.post(`${this.apiBaseUrl}/registrar-assinatura`, null, {
                    params: { numeroPapeleta, usuario }
                })
            );
        } catch (error) {
            console.error('Erro ao registrar assinatura:', error);
        }
    }

    // Adicione este método no WebSocketService
    async buscarHistoricoSolicitacoes(papeleta: string): Promise<SolicitacaoExclusaoDTO[]> {
        if (!this.apiBaseUrl) {
            console.log('⏳ Aguardando configuração da API...');
            return [];
        }

        try {
            const url = `${this.apiBaseUrl}/historico/${papeleta}`;
            console.log('📡 Buscando histórico para papeleta:', papeleta);

            const response = await firstValueFrom(this.http.get<SolicitacaoExclusaoDTO[]>(url));
            console.log('✅ Histórico recebido:', response);
            return response;
        } catch (error) {
            console.error('❌ Erro ao buscar histórico:', error);
            return [];
        }
    }

    // No WebSocketService, adicione este método
    async buscarHistoricoPorFolha(folhaServicoId: number): Promise<SolicitacaoExclusaoDTO[]> {
        if (!this.apiBaseUrl) {
            console.log('⏳ Aguardando configuração da API...');
            return [];
        }

        try {
            const url = `${this.apiBaseUrl}/historico/folha/${folhaServicoId}`;
            console.log('📡 Buscando histórico para folha:', folhaServicoId);

            const response = await firstValueFrom(this.http.get<SolicitacaoExclusaoDTO[]>(url));
            console.log('✅ Histórico da folha recebido:', response);
            return response;
        } catch (error) {
            console.error('❌ Erro ao buscar histórico por folha:', error);
            return [];
        }
    }

    async carregarSolicitacoesPendentes(): Promise<SolicitacaoExclusao[]> {
        if (!this.apiBaseUrl) {
            console.log('⏳ Aguardando configuração da API...');
            return [];
        }

        try {
            const url = `${this.apiBaseUrl}/pendentes`;
            console.log('📡 Chamando API:', url);

            const response = await firstValueFrom(this.http.get<SolicitacaoExclusao[]>(url));

            // 🔥 GARANTE que só retorna EXCLUSAO com status PENDENTE
            const exclusoesPendentes = response.filter(s =>
                s.tipoMarcacao === 'EXCLUSAO' && s.status === 'PENDENTE'
            );

            console.log('✅ Solicitações de EXCLUSÃO pendentes:', exclusoesPendentes.length);
            return exclusoesPendentes;
        } catch (error) {
            console.error('❌ Erro ao carregar solicitações pendentes:', error);
            return [];
        }
    }
}